import React, {
  useState,
} from "react"

import {
  PlusIcon,
  MinusIcon,
} from "@heroicons/react/24/outline"

import PulseLoader
  from "react-spinners/PulseLoader"

import {
  useGetOrgIntentionQuestionsQuery,
} from "@/features/orgIntentionBoards/orgIntentionQuestionsApiSlice"


const EtOrgIntentionBoardResponsePreview = ({
  board,
}) => {

  // First Created - 09 Sep 2026
  // Author - Atul Pandey
  // Purpose - Read only preview of the candidate facing Trinity
  //           Intention Board response experience.

  /*
  |--------------------------------------------------------------------------
  | 01 - Before You Begin Expansion
  |--------------------------------------------------------------------------
  */

  const [
    showFullBoardNarrative,
    setShowFullBoardNarrative,
  ] =
    useState(
      false
    )

  /*
  |--------------------------------------------------------------------------
  | 02 - User Timezone
  |--------------------------------------------------------------------------
  */

  const userTimezone =
    Intl
      .DateTimeFormat()
      .resolvedOptions()
      .timeZone ||
    "UTC"

  /*
  |--------------------------------------------------------------------------
  | 03 - Current Date / Time
  |--------------------------------------------------------------------------
  */

  const currentDateTime =
    new Date()

  /*
  |--------------------------------------------------------------------------
  | 04 - Board
  |--------------------------------------------------------------------------
  */

  const intentionBoardId =
    board
      ?._id

  const boardTimezone =
    board
      ?.timezone ||
    "America/Los_Angeles"

  /*
  |--------------------------------------------------------------------------
  | 05 - Questions
  |--------------------------------------------------------------------------
  */

  const {
    data:
      orgIntentionQuestionsData,

    isLoading:
      isLoadingQuestions,

    isError:
      isQuestionsError,

    error:
      questionsError,
  } =
    useGetOrgIntentionQuestionsQuery(
      {
        intentionBoardId,
      },
      {
        skip:
          !intentionBoardId,
      }
    )

  /*
  |--------------------------------------------------------------------------
  | 06 - Normalize Questions
  |--------------------------------------------------------------------------
  */

  const questions =
    orgIntentionQuestionsData
      ?.ids
      ?.map(
        id =>
          orgIntentionQuestionsData
            ?.entities[id]
      )
      ?.filter(
        question =>
          question
            ?.isActive !==
          false
      )
      ?.sort(
        (
          a,
          b
        ) =>
          (
            a
              ?.displaySequence ||
            a
              ?.questionNumber ||
            0
          ) -
          (
            b
              ?.displaySequence ||
            b
              ?.questionNumber ||
            0
          )
      ) ||
    []

  /*
  |--------------------------------------------------------------------------
  | 07 - Format Date / Time
  |--------------------------------------------------------------------------
  */

  const formatDateTime =
    (
      dateValue,
      timezone
    ) => {

      if (
        !dateValue
      ) {

        return "Not Available"

      }

      try {

        return new Intl.DateTimeFormat(
          undefined,
          {
            timeZone:
              timezone,

            year:
              "numeric",

            month:
              "short",

            day:
              "numeric",

            hour:
              "numeric",

            minute:
              "2-digit",

            hour12:
              true,

            timeZoneName:
              "short",
          }
        )
          .format(
            new Date(
              dateValue
            )
          )

      } catch (
        error
      ) {

        console.error(
          error
        )

        return new Date(
          dateValue
        )
          .toLocaleString()

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 08 - Format Duration
  |--------------------------------------------------------------------------
  */

  const formatDuration =
    durationMilliseconds => {

      if (
        durationMilliseconds <=
        0
      ) {

        return "0 min"

      }

      const totalMinutes =
        Math.floor(
          durationMilliseconds /
          (
            60 *
            1000
          )
        )

      const days =
        Math.floor(
          totalMinutes /
          (
            24 *
            60
          )
        )

      const remainingAfterDays =
        totalMinutes %
        (
          24 *
          60
        )

      const hours =
        Math.floor(
          remainingAfterDays /
          60
        )

      const minutes =
        remainingAfterDays %
        60

      const parts = []

      if (
        days >
        0
      ) {

        parts.push(
          `${days} ${
            days ===
            1
              ? "day"
              : "days"
          }`
        )

      }

      if (
        hours >
        0
      ) {

        parts.push(
          `${hours} ${
            hours ===
            1
              ? "hr"
              : "hrs"
          }`
        )

      }

      if (
        minutes >
        0
      ) {

        parts.push(
          `${minutes} min`
        )

      }

      if (
        !parts.length
      ) {

        return "Less than 1 min"

      }

      return parts.join(
        ", "
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 09 - Remaining Time
  |--------------------------------------------------------------------------
  */

  const deadlineDate =
    board
      ?.responseDeadline
      ? new Date(
          board.responseDeadline
        )
      : null

  const deadlinePassed =
    deadlineDate
      ? deadlineDate.getTime() <=
        currentDateTime.getTime()
      : false

  const remainingTime =
    deadlineDate &&
    !deadlinePassed
      ? formatDuration(
          deadlineDate.getTime() -
          currentDateTime.getTime()
        )
      : "Deadline reached"

  /*
  |--------------------------------------------------------------------------
  | 10 - Response Window Preview
  |--------------------------------------------------------------------------
  |
  | This is a board level preview only.
  |
  | Candidate specific response lifecycle is intentionally not simulated.
  |
  */

  const responseWindowOpen =
    board
      ?.isActive !==
      false &&
    board
      ?.boardStatus ===
      "open" &&
    !deadlinePassed

  /*
  |--------------------------------------------------------------------------
  | 11 - Response Window Timeline
  |--------------------------------------------------------------------------
  */

  const openedDate =
    board
      ?.responseOpenedAt
      ? new Date(
          board.responseOpenedAt
        )
      : null

  const hasResponseWindowTimeline =
    Boolean(
      openedDate &&
      deadlineDate &&
      deadlineDate.getTime() >
        openedDate.getTime()
    )

  const responseWindowDuration =
    hasResponseWindowTimeline
      ? (
          deadlineDate.getTime() -
          openedDate.getTime()
        )
      : 0

  const elapsedResponseWindow =
    hasResponseWindowTimeline
      ? (
          currentDateTime.getTime() -
          openedDate.getTime()
        )
      : 0

  const rawTimelinePercentage =
    responseWindowDuration >
    0
      ? (
          elapsedResponseWindow /
          responseWindowDuration
        ) *
        100
      : 0

  const timelinePercentage =
    Math.min(
      100,
      Math.max(
        0,
        rawTimelinePercentage
      )
    )

  const timelineLabelPercentage =
    Math.min(
      92,
      Math.max(
        8,
        timelinePercentage
      )
    )

  /*
  |--------------------------------------------------------------------------
  | 12 - Question Type Label
  |--------------------------------------------------------------------------
  */

  const getQuestionTypeLabel =
    questionType => {

      switch (
        questionType
      ) {

        case "reflection":
          return "Reflection"

        case "short_text":
          return "Short Response"

        case "long_text":
          return "Long Response"

        case "single_select":
          return "Select One"

        case "multi_select":
          return "Select Multiple"

        case "checkbox_confirmation":
          return "Confirmation"

        default:
          return questionType

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 13 - Create Markup
  |--------------------------------------------------------------------------
  */

  const createMarkup =
    htmlContent => {

      return {
        __html:
          htmlContent,
      }

    }

  /*
  |--------------------------------------------------------------------------
  | 14 - Loading
  |--------------------------------------------------------------------------
  */

  if (
    !board
  ) {

    return null

  }

  /*
  |--------------------------------------------------------------------------
  | 15 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="bg-gray-100 rounded-lg p-4 font-poppins">

      <div className="max-w-4xl mx-auto mb-6">

        {/*
        |--------------------------------------------------------------------------
        | Preview Banner
        |--------------------------------------------------------------------------
        */}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">

          <p className="text-xs font-bold text-blue-700">
            Response Board Preview
          </p>

          <p className="text-[10px] text-blue-600 mt-1">
            This is how this Intention Board will appear to a nominated
            candidate after they verify their access. Response fields are
            disabled in preview mode.
          </p>

        </div>

        {/*
        |--------------------------------------------------------------------------
        | Header
        |--------------------------------------------------------------------------
        */}

        <div className="bg-white rounded-lg shadow-md p-6">

          <p className="text-sm font-bold text-orange-600">
            Trinity
          </p>

          <p className="text-2xl font-bold text-gray-800 mt-1">

            {
              board
                ?.headline ||
              "Intention Board"
            }

          </p>

          <p className="text-sm text-gray-600 mt-3">

            Welcome,{" "}

            <span className="font-bold">
              Candidate Name
            </span>

          </p>

          {/*
          |--------------------------------------------------------------------------
          | Status
          |--------------------------------------------------------------------------
          */}

          <div className="flex justify-start items-center flex-wrap mt-4">

            <p className="text-[10px] font-bold rounded-full px-3 py-1 mr-2 bg-gray-100 text-gray-600">
              Not Started
            </p>

            <p
              className={`text-[10px] font-bold rounded-full px-3 py-1 mr-2 ${
                board
                  ?.boardStatus ===
                  "open"
                  ? "bg-lime-100 text-lime-700"
                  : "bg-yellow-100 text-yellow-700"
              }`}
            >

              Board: {
                board
                  ?.boardStatus ||
                "Unknown"
              }

            </p>

            <p
              className={`text-[10px] font-bold rounded-full px-3 py-1 ${
                responseWindowOpen
                  ? "bg-lime-100 text-lime-700"
                  : "bg-gray-200 text-gray-600"
              }`}
            >

              {
                responseWindowOpen
                  ? "Response Window Open"
                  : "Response Window Closed"
              }

            </p>

          </div>

          {/*
          |--------------------------------------------------------------------------
          | Response Deadline
          |--------------------------------------------------------------------------
          */}

          {board
            ?.responseDeadline && (

            <div className="mt-4 bg-gray-50 rounded p-4">

              <p className="text-xs font-bold text-gray-800">
                Response Deadline
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">

                {/*
                |--------------------------------------------------------------------------
                | Board Time
                |--------------------------------------------------------------------------
                */}

                <div className="bg-white rounded p-3">

                  <p className="text-[9px] font-bold text-gray-500 uppercase">
                    Board Time
                  </p>

                  <p className="text-xs font-bold text-gray-700 mt-1">

                    {
                      formatDateTime(
                        board
                          .responseDeadline,
                        boardTimezone
                      )
                    }

                  </p>

                  <p className="text-[9px] text-gray-500 mt-1">
                    {
                      boardTimezone
                    }
                  </p>

                </div>

                {/*
                |--------------------------------------------------------------------------
                | User Time
                |--------------------------------------------------------------------------
                */}

                <div className="bg-white rounded p-3">

                  <p className="text-[9px] font-bold text-orange-600 uppercase">
                    Your Time
                  </p>

                  <p className="text-xs font-bold text-gray-700 mt-1">

                    {
                      formatDateTime(
                        board
                          .responseDeadline,
                        userTimezone
                      )
                    }

                  </p>

                  <p className="text-[9px] text-gray-500 mt-1">
                    {
                      userTimezone
                    }
                  </p>

                </div>

                {/*
                |--------------------------------------------------------------------------
                | Current Time
                |--------------------------------------------------------------------------
                */}

                <div className="bg-white rounded p-3">

                  <p className="text-[9px] font-bold text-gray-500 uppercase">
                    Current Time Where You Are
                  </p>

                  <p className="text-xs font-bold text-gray-700 mt-1">

                    {
                      formatDateTime(
                        currentDateTime,
                        userTimezone
                      )
                    }

                  </p>

                  <p className="text-[9px] text-gray-500 mt-1">
                    {
                      userTimezone
                    }
                  </p>

                </div>

              </div>

              {/*
              |--------------------------------------------------------------------------
              | Active Response Window Timeline
              |--------------------------------------------------------------------------
              */}

              {!deadlinePassed && (

                <div className="bg-white rounded p-4 mt-3">

                  {hasResponseWindowTimeline ? (

                    <>

                      <div className="relative h-5">

                        <p className="absolute left-0 text-[9px] font-bold text-gray-500 uppercase">
                          Opened
                        </p>

                        <p
                          className="absolute text-[9px] font-bold text-orange-700 uppercase -translate-x-1/2"
                          style={{
                            left:
                              `${timelineLabelPercentage}%`,
                          }}
                        >
                          Now
                        </p>

                        <p className="absolute right-0 text-[9px] font-bold text-gray-500 uppercase">
                          Deadline
                        </p>

                      </div>

                      <div className="relative h-5 mt-1">

                        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-gray-300" />

                        <div
                          className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-orange-500"
                          style={{
                            width:
                              `${timelinePercentage}%`,
                          }}
                        />

                        <div className="absolute left-0 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-orange-600" />

                        <div
                          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-orange-600 border-2 border-white shadow"
                          style={{
                            left:
                              `${timelinePercentage}%`,
                          }}
                        />

                        <div className="absolute right-0 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-orange-600" />

                      </div>

                      <div className="grid grid-cols-3 gap-3 mt-2">

                        <div>

                          <p className="text-[9px] text-gray-500">

                            {
                              formatDateTime(
                                openedDate,
                                userTimezone
                              )
                            }

                          </p>

                        </div>

                        <div className="text-center">

                          <p className="text-[10px] font-bold text-orange-700">

                            {
                              Math.round(
                                timelinePercentage
                              )
                            }
                            % elapsed

                          </p>

                        </div>

                        <div className="text-right">

                          <p className="text-xs font-bold text-orange-700">
                            {
                              remainingTime
                            }
                          </p>

                          <p className="text-[9px] text-gray-500 mt-1">
                            remaining
                          </p>

                        </div>

                      </div>

                    </>

                  ) : (

                    <div>

                      <p className="text-[9px] font-bold text-gray-500 uppercase">
                        Time Remaining
                      </p>

                      <p className="text-sm font-bold text-orange-700 mt-1">
                        {
                          remainingTime
                        }
                      </p>

                    </div>

                  )}

                </div>

              )}

            </div>

          )}

        </div>

        {/*
        |--------------------------------------------------------------------------
        | Before You Begin
        |--------------------------------------------------------------------------
        */}

        {board
          ?.boardNarrative && (

          <div className="bg-white rounded-lg shadow-md p-6 mt-4">

            <div className="flex justify-start items-center mb-3">

              <div
                onClick={
                  () =>
                    setShowFullBoardNarrative(
                      prev =>
                        !prev
                    )
                }
                className="
                  h-7
                  w-7
                  rounded-full
                  flex
                  justify-center
                  items-center
                  cursor-pointer
                  hover:bg-gray-100
                "
                title={
                  showFullBoardNarrative
                    ? "Show less"
                    : "Show more"
                }
              >

                {showFullBoardNarrative ? (

                  <MinusIcon
                    className="h-5 w-5 text-orange-600"
                  />

                ) : (

                  <PlusIcon
                    className="h-5 w-5 text-orange-600"
                  />

                )}

              </div>

              <p className="text-sm font-bold text-gray-800 mx-2">
                Before You Begin
              </p>

            </div>

            <div>

              <div
                className={`
                  text-sm
                  text-gray-700
                  leading-relaxed
                  [&_p]:mb-2
                  [&_ul]:list-disc
                  [&_ul]:ml-5
                  [&_ul]:mb-2
                  [&_ol]:list-decimal
                  [&_ol]:ml-5
                  [&_ol]:mb-2
                  [&_li]:mb-1
                  [&_strong]:font-bold
                  [&_a]:text-orange-600
                  [&_a]:underline
                  ${
                    showFullBoardNarrative
                      ? ""
                      : "line-clamp-3"
                  }
                `}
                dangerouslySetInnerHTML={
                  createMarkup(
                    board
                      .boardNarrative
                  )
                }
              />

              <div className="flex justify-end items-center mt-2">

                <button
                  type="button"
                  onClick={
                    () =>
                      setShowFullBoardNarrative(
                        prev =>
                          !prev
                      )
                  }
                  className="
                    text-xs
                    font-bold
                    text-orange-600
                    hover:text-orange-700
                    hover:underline
                    cursor-pointer
                  "
                >

                  {
                    showFullBoardNarrative
                      ? "Read Less"
                      : "Read More"
                  }

                </button>

              </div>

            </div>

          </div>

        )}

        {/*
        |--------------------------------------------------------------------------
        | Response Progress
        |--------------------------------------------------------------------------
        */}

        <div className="bg-white rounded-lg shadow-md p-4 mt-4">

          <div className="flex justify-between items-center">

            <div>

              <p className="text-xs font-bold text-gray-700">
                Your Response
              </p>

              <p className="text-[10px] text-gray-500 mt-1">
                Completion: 0%
              </p>

            </div>

            <p className="text-[10px] font-bold rounded-full px-3 py-1 bg-gray-100 text-gray-600">
              Not Started
            </p>

          </div>

        </div>

        {/*
        |--------------------------------------------------------------------------
        | Questions
        |--------------------------------------------------------------------------
        */}

        <div className="bg-white rounded-lg shadow-md p-6 mt-4">

          <div className="mb-5">

            <p className="text-lg font-bold text-gray-800">
              Your Reflection
            </p>

            <p className="text-xs text-gray-500 mt-1">

              {
                questions.length
              }{" "}

              question{
                questions.length ===
                1
                  ? ""
                  : "s"
              }

            </p>

          </div>

          {isLoadingQuestions && (

            <div className="py-3">

              <PulseLoader
                color={"#ea580c"}
                size={6}
              />

            </div>

          )}

          {isQuestionsError && (

            <div className="bg-orange-50 rounded p-3">

              <p className="text-xs text-orange-700">

                {
                  questionsError
                    ?.data
                    ?.message ||
                  "Unable to load Intention Board questions."
                }

              </p>

            </div>

          )}

          {!isLoadingQuestions &&
            !isQuestionsError &&
            questions.length ===
            0 && (

            <div className="bg-gray-50 rounded p-4">

              <p className="text-xs text-gray-500">
                No questions are currently available.
              </p>

            </div>

          )}

          {questions
            ?.map(
              (
                question,
                index
              ) => {

                return (

                  <div
                    key={
                      question
                        ?._id
                    }
                    className="border-b border-gray-200 py-5 last:border-b-0"
                  >

                    {/*
                    |--------------------------------------------------------------------------
                    | Question
                    |--------------------------------------------------------------------------
                    */}

                    <div className="flex justify-start items-center flex-wrap">

                      <p className="text-sm font-bold text-gray-800">

                        {
                          question
                            ?.questionNumber ||
                          index +
                          1
                        }
                        .{" "}
                        {
                          question
                            ?.questionText
                        }

                      </p>

                      {question
                        ?.isRequired && (

                        <p className="text-[9px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">
                          Required
                        </p>

                      )}

                    </div>

                    {question
                      ?.questionDescription && (

                      <p className="text-xs text-gray-600 mt-2">

                        {
                          question
                            .questionDescription
                        }

                      </p>

                    )}

                    {question
                      ?.responseGuidance && (

                      <p className="text-[10px] italic text-gray-500 mt-2">

                        {
                          question
                            .responseGuidance
                        }

                      </p>

                    )}

                    <p className="text-[9px] text-gray-400 mt-2">

                      {
                        getQuestionTypeLabel(
                          question
                            ?.questionType
                        )
                      }

                    </p>

                    {/*
                    |--------------------------------------------------------------------------
                    | Text Response
                    |--------------------------------------------------------------------------
                    */}

                    {(
                      question
                        ?.questionType ===
                        "reflection" ||
                      question
                        ?.questionType ===
                        "short_text" ||
                      question
                        ?.questionType ===
                        "long_text"
                    ) && (

                      <textarea
                        value={""}
                        readOnly
                        disabled
                        placeholder="Enter your response"
                        rows={
                          question
                            ?.questionType ===
                            "short_text"
                            ? 2
                            : 5
                        }
                        className="w-full rounded border p-3 text-sm mt-3 bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                      />

                    )}

                    {/*
                    |--------------------------------------------------------------------------
                    | Single Select
                    |--------------------------------------------------------------------------
                    */}

                    {question
                      ?.questionType ===
                      "single_select" && (

                      <div className="space-y-2 mt-3">

                        {question
                          ?.options
                          ?.map(
                            (
                              option,
                              optionIndex
                            ) => (

                              <label
                                key={
                                  optionIndex
                                }
                                className="flex items-center text-xs text-gray-700"
                              >

                                <input
                                  type="radio"
                                  disabled
                                  className="mr-2"
                                />

                                {
                                  option
                                    ?.optionLabel
                                }

                              </label>

                            )
                          )}

                      </div>

                    )}

                    {/*
                    |--------------------------------------------------------------------------
                    | Multi Select
                    |--------------------------------------------------------------------------
                    */}

                    {question
                      ?.questionType ===
                      "multi_select" && (

                      <div className="space-y-2 mt-3">

                        {question
                          ?.options
                          ?.map(
                            (
                              option,
                              optionIndex
                            ) => (

                              <label
                                key={
                                  optionIndex
                                }
                                className="flex items-center text-xs text-gray-700"
                              >

                                <input
                                  type="checkbox"
                                  disabled
                                  className="mr-2"
                                />

                                {
                                  option
                                    ?.optionLabel
                                }

                              </label>

                            )
                          )}

                      </div>

                    )}

                    {/*
                    |--------------------------------------------------------------------------
                    | Confirmation
                    |--------------------------------------------------------------------------
                    */}

                    {question
                      ?.questionType ===
                      "checkbox_confirmation" && (

                      <label className="flex items-center text-xs text-gray-700 mt-3">

                        <input
                          type="checkbox"
                          disabled
                          className="mr-2"
                        />

                        Confirm

                      </label>

                    )}

                  </div>

                )

              }
            )}

          {/*
          |--------------------------------------------------------------------------
          | Response Actions Preview
          |--------------------------------------------------------------------------
          */}

          {questions.length >
            0 && (

            <div className="border-t border-gray-200 pt-5 mt-3">

              <div className="flex justify-start items-center flex-wrap gap-2">

                <button
                  type="button"
                  disabled
                  className="rounded px-5 py-2 text-sm font-bold bg-gray-300 text-gray-500 cursor-not-allowed"
                >
                  Save Draft
                </button>

                <button
                  type="button"
                  disabled
                  className="rounded px-5 py-2 text-sm font-bold bg-gray-300 text-gray-500 cursor-not-allowed"
                >
                  Submit Response
                </button>

              </div>

              <div className="mt-2">

                <p className="text-[10px] text-gray-500">
                  Save Draft allows you to return and continue your response
                  until the deadline.
                </p>

                <p className="text-[10px] text-gray-500 mt-1">
                  Submit Response is final and your responses cannot be edited
                  afterward.
                </p>

              </div>

            </div>

          )}

        </div>

        {/*
        |--------------------------------------------------------------------------
        | Closing Narrative
        |--------------------------------------------------------------------------
        */}

        {board
          ?.closingNarrative && (

          <div className="bg-orange-50 border border-orange-100 rounded-lg p-5 mt-4">

            <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">

              {
                board
                  .closingNarrative
              }

            </div>

          </div>

        )}

      </div>

    </div>

  )

}

export default EtOrgIntentionBoardResponsePreview